import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "../../lib/api";
import {
  Settings2, UserPlus, Trash2, Loader2, ShieldCheck, Download, ScrollText,
  Crown, Shield, User, Eye, Check, X, Filter, Webhook, Send
} from "lucide-react";

const ROLE_META = {
  owner:  { icon: Crown,  color: "bg-amber-300",   blurb: "Full control incl. billing" },
  admin:  { icon: Shield, color: "bg-purple-200",  blurb: "Edit, delete, share, manage members" },
  member: { icon: User,   color: "bg-emerald-200", blurb: "Run workflows, chat with workers" },
  viewer: { icon: Eye,    color: "bg-slate-200",   blurb: "Read-only" },
};

/**
 * /workforce/settings — Workspace roles (Owner/Admin/Member/Viewer) + Audit log.
 */
export default function WorkspaceSettings() {
  const [tab, setTab] = useState("members");
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <Settings2 size={12} strokeWidth={2.5} /> Workspace
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="settings-title">Roles &amp; audit</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Invite teammates into your workforce with the right level of access, and keep an exportable record of every action.</p>
      </div>

      <div className="flex gap-1 border-b-2 border-slate-900" data-testid="settings-tabs">
        {[["members", "Members & roles", ShieldCheck], ["outbound", "Outbound hooks", Webhook], ["audit", "Audit log", ScrollText]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`settings-tab-${id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 font-heading font-bold text-sm border-b-4 -mb-0.5 transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Icon size={14} strokeWidth={2.5} /> {label}
          </button>
        ))}
      </div>

      {tab === "members" ? <MembersPanel /> : tab === "outbound" ? <OutboundPanel /> : <AuditPanel />}
    </div>
  );
}

function OutboundPanel() {
  const [cfg, setCfg] = useState({ webhook_url: "", events: [], enabled: true });
  const [events, setEvents] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/workspace/outbound").then((r) => { setCfg(r.data.outbound); setEvents(r.data.available_events); }).catch(() => {});
    api.get("/workspace/outbound/deliveries").then((r) => setDeliveries(r.data.deliveries)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/workspace/outbound", cfg); setCfg(data.outbound); }
    catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/workspace/outbound/test", { url: cfg.webhook_url }); setResult(data); load(); }
    catch (e) { setResult({ ok: false, error: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const toggleEvent = (ev) => setCfg({ ...cfg, events: cfg.events.includes(ev) ? cfg.events.filter((x) => x !== ev) : [...cfg.events, ev] });

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="brut-card p-4 space-y-4" data-testid="outbound-config">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">Workspace default hook (Zapier · Make · n8n · any URL)</div>
          <p className="text-xs text-slate-600">Every workflow in your workspace POSTs a JSON event here when it finishes or pauses for approval — unless the workflow has its own hook set in its Notify settings.</p>
          <div className="flex gap-2">
            <input className="brut-input flex-1 font-mono text-sm" placeholder="https://hooks.zapier.com/hooks/catch/123/abc"
                   value={cfg.webhook_url} onChange={(e) => setCfg({ ...cfg, webhook_url: e.target.value })} data-testid="outbound-url" />
            <button onClick={test} disabled={busy || !cfg.webhook_url.startsWith("http")} className="brut-btn text-xs bg-white text-slate-900 disabled:opacity-40" data-testid="outbound-test">
              <Send size={12} strokeWidth={2.5} /> Send test
            </button>
          </div>
          {result && (
            <div className={`text-xs font-mono ${result.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="outbound-test-result">
              {result.ok ? `Delivered · HTTP ${result.status_code}` : `Failed · ${result.status_code || ""} ${result.error || ""}`}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {events.map((ev) => (
              <label key={ev} className="inline-flex items-center gap-2 text-xs font-mono">
                <input type="checkbox" checked={cfg.events.includes(ev)} onChange={() => toggleEvent(ev)} data-testid={`outbound-event-${ev}`} /> {ev}
              </label>
            ))}
            <label className="inline-flex items-center gap-2 text-xs font-bold ml-auto">
              <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="outbound-enabled" /> Enabled
            </label>
          </div>
          <button onClick={save} disabled={busy} className="brut-btn text-sm" data-testid="outbound-save">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />} Save default hook
          </button>
        </div>

        <div className="brut-card p-0 overflow-hidden" data-testid="outbound-deliveries">
          <div className="px-4 py-2 bg-slate-100 border-b-2 border-slate-900 text-[10px] uppercase tracking-widest font-bold text-slate-600">Recent deliveries</div>
          {deliveries.length === 0 ? (
            <div className="px-4 py-6 text-center text-slate-500 italic text-sm" data-testid="outbound-deliveries-empty">No deliveries yet — send a test above.</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.delivery_id} className="border-b border-slate-200" data-testid={`delivery-${d.delivery_id}`}>
                    <td className="px-4 py-2 font-mono text-slate-500 whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2"><span className={`font-bold ${d.ok ? "text-emerald-600" : "text-red-600"}`}>{d.ok ? "OK" : "FAIL"}</span> {d.status_code || ""}</td>
                    <td className="px-4 py-2 font-mono">{d.event}</td>
                    <td className="px-4 py-2 text-slate-500">{d.scope}</td>
                    <td className="px-4 py-2 font-mono truncate max-w-[260px]" title={d.url}>{d.url}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="brut-card p-4 space-y-2" data-testid="outbound-payload-doc">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">Payload shape</div>
        <pre className="text-[10px] font-mono bg-slate-900 text-emerald-200 rounded-lg p-3 overflow-x-auto">{`{
  "event": "run.completed",
  "run": {
    "run_id": "run_…",
    "workflow_name": "Research brief",
    "status": "completed",
    "input": "NVIDIA earnings",
    "final_output": "…",
    "total_cost": 0.0123,
    "url": "https://…/workforce/runs/run_…"
  },
  "approval": null
}`}</pre>
        <p className="text-[11px] text-slate-500">In Zapier pick <b>Webhooks by Zapier → Catch Hook</b>; in Make use a <b>Custom webhook</b>. Send a test to auto-detect fields.</p>
      </div>
    </div>
  );
}

function MembersPanel() {
  const [data, setData] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get("/workspace/members").then((r) => setData(r.data)).catch(() => {});
    api.get("/workspace/memberships").then((r) => setMemberships(r.data.memberships)).catch(() => {});
    api.get("/workspace/permissions").then((r) => setMatrix(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/workspace/members", { email: email.trim(), role });
      setEmail(""); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : (d || "Invite failed"));
    } finally { setBusy(false); }
  };
  const changeRole = async (uid, r) => { await api.patch(`/workspace/members/${uid}`, { role: r }); load(); };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from your workspace?`)) return;
    await api.delete(`/workspace/members/${m.user_id}`); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="brut-card p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">Invite a teammate</div>
          <div className="flex gap-2 flex-wrap">
            <input className="brut-input flex-1 min-w-[200px]" placeholder="teammate@company.com (must have an AiMedley account)"
                   value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
            <select className="brut-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role">
              <option value="admin">Admin</option><option value="member">Member</option><option value="viewer">Viewer</option>
            </select>
            <button onClick={invite} disabled={busy || !email.includes("@")} className="brut-btn text-sm disabled:opacity-40" data-testid="invite-submit">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} strokeWidth={3} />} Invite
            </button>
          </div>
          {err && <div className="text-sm text-red-600" data-testid="invite-error">{err}</div>}
        </div>

        <div className="brut-card p-0 overflow-hidden" data-testid="members-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b-2 border-slate-900 text-[10px] uppercase tracking-widest font-bold text-slate-600">
              <tr><th className="text-left px-4 py-2">Person</th><th className="text-left px-4 py-2">Role</th><th className="px-4 py-2" /></tr>
            </thead>
            <tbody>
              {data && (
                <tr className="border-b border-slate-200">
                  <td className="px-4 py-3 font-heading font-bold">{data.owner.name} <span className="text-slate-400 font-normal text-xs">(you)</span></td>
                  <td className="px-4 py-3"><RoleChip role="owner" /></td><td />
                </tr>
              )}
              {(data?.members || []).map((m) => (
                <tr key={m.user_id} className="border-b border-slate-200" data-testid={`member-${m.user_id}`}>
                  <td className="px-4 py-3"><div className="font-heading font-bold">{m.name}</div><div className="text-xs text-slate-500">{m.email}</div></td>
                  <td className="px-4 py-3">
                    <select className="brut-input py-1 text-xs" value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)} data-testid={`member-role-${m.user_id}`}>
                      <option value="admin">Admin</option><option value="member">Member</option><option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(m)} className="text-red-500 hover:text-red-700" data-testid={`member-remove-${m.user_id}`}><Trash2 size={14} strokeWidth={2.5} /></button>
                  </td>
                </tr>
              ))}
              {data && data.members.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500 italic" data-testid="members-empty">Just you so far. Invite someone above.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {memberships.length > 0 && (
          <div className="brut-card p-4 space-y-2" data-testid="memberships-list">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">Workspaces you've joined</div>
            {memberships.map((m) => (
              <div key={m.owner_id} className="flex items-center gap-3 text-sm">
                <RoleChip role={m.role} />
                <span className="font-heading font-bold">{m.owner_name}'s workspace</span>
                <span className="text-xs text-slate-500">{m.workflows} workflows · {m.workers} workers</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="brut-card p-4 space-y-3" data-testid="permissions-matrix">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">What each role can do</div>
        {matrix && (
          <table className="w-full text-[11px]">
            <thead><tr><th className="text-left py-1" />{matrix.roles.map((r) => <th key={r} className="py-1 capitalize font-bold">{r[0].toUpperCase()}</th>)}</tr></thead>
            <tbody>
              {Object.keys(matrix.matrix.owner).map((perm) => (
                <tr key={perm} className="border-t border-slate-200">
                  <td className="py-1.5 capitalize text-slate-600">{perm.replace("_", " ")}</td>
                  {matrix.roles.map((r) => (
                    <td key={r} className="text-center">{matrix.matrix[r][perm] ? <Check size={12} strokeWidth={3} className="inline text-emerald-600" /> : <X size={12} className="inline text-slate-300" />}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="space-y-1.5 pt-2 border-t-2 border-dashed border-slate-200">
          {Object.entries(ROLE_META).map(([r, m]) => (
            <div key={r} className="flex items-center gap-2 text-xs"><RoleChip role={r} /><span className="text-slate-600">{m.blurb}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RoleChip({ role }) {
  const m = ROLE_META[role] || ROLE_META.viewer;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-slate-900 text-[10px] font-bold uppercase tracking-widest ${m.color}`}>
      <Icon size={10} strokeWidth={3} /> {role}
    </span>
  );
}

function AuditPanel() {
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filter) params.action = filter;
      if (before) params.before = before;
      const { data } = await api.get("/audit", { params });
      setEntries((prev) => before ? [...prev, ...data.entries] : data.entries);
      setHasMore(data.has_more);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/audit/actions").then((r) => setActions(r.data.actions)).catch(() => {});
    api.get("/audit/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    const { data } = await api.get("/audit/export.csv", { params: filter ? { action: filter } : {}, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "aimedley-audit.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="brut-card px-4 py-2 text-sm" data-testid="audit-total"><span className="font-mono font-bold text-xl">{summary?.total ?? "—"}</span> <span className="text-slate-500">events</span></div>
        <div className="flex-1 flex items-center gap-2 flex-wrap">
          {(summary?.by_action || []).slice(0, 5).map((a) => (
            <button key={a.action} onClick={() => setFilter(filter === a.action ? "" : a.action)}
                    className={`text-[10px] font-mono border-2 border-slate-900 rounded-full px-2 py-0.5 ${filter === a.action ? "bg-slate-900 text-white" : "bg-white"}`}>
              {a.action} · {a.count}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1">
          <Filter size={12} />
          <select className="brut-input py-1 text-xs" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter">
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button onClick={exportCsv} className="brut-btn text-xs" data-testid="audit-export"><Download size={12} strokeWidth={3} /> Export CSV</button>
      </div>

      <div className="brut-card p-0 overflow-hidden" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b-2 border-slate-900 text-[10px] uppercase tracking-widest font-bold text-slate-600">
            <tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Actor</th><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Resource</th><th className="text-left px-4 py-2">Details</th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.audit_id} className="border-b border-slate-200 align-top" data-testid={`audit-${e.audit_id}`}>
                <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap font-mono">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 font-heading font-bold text-xs">{e.actor_name}{e.source !== "web" && <span className="ml-1 text-[9px] bg-purple-200 border border-slate-900 rounded-full px-1.5">{e.source}</span>}</td>
                <td className="px-4 py-2"><code className="text-[11px] bg-slate-100 border border-slate-200 rounded px-1">{e.action}</code></td>
                <td className="px-4 py-2 text-xs text-slate-600"><div>{e.resource_type}</div><div className="font-mono text-[10px] text-slate-400">{e.resource_id}</div></td>
                <td className="px-4 py-2 text-xs text-slate-600">{Object.entries(e.meta || {}).map(([k, v]) => <div key={k}><span className="text-slate-400">{k}:</span> {String(v)}</div>)}</td>
              </tr>
            ))}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 italic" data-testid="audit-empty">No events yet. Create a worker or run a workflow and it will show up here.</td></tr>
            )}
          </tbody>
        </table>
        {hasMore && (
          <div className="p-3 text-center border-t border-slate-200">
            <button onClick={() => load(entries[entries.length - 1]?.created_at)} disabled={loading} className="brut-btn text-xs bg-white text-slate-900" data-testid="audit-load-more">
              {loading ? <Loader2 size={12} className="animate-spin" /> : null} Load more
            </button>
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Endpoint: <code>{API}/audit</code> · CSV export includes up to 5,000 most recent events.</p>
    </div>
  );
}
